/// <amd-module name="Pipeline/Analytics/PipelineStepsDurationCard/Sources/PipelineStepsDurationCardSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import * as PipelineEntities from "Analytics/Shared/Common/PipelineEntities";
import { IReportContext } from "Analytics/Shared/Common/Types";
import { getDateFilter } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { expr } from "Analytics/Shared/FilterExpression";
import { ODataQuery } from "Analytics/Shared/QueryBuilder";
import { IFilterItem, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import { DateSKParser } from "Analytics/Shared/Utils/DateSKParser";
import { percentile_value } from "Pipeline/Analytics/Common/Constants";
import * as CommonResources from "Pipeline/Analytics/Common/Resources";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { PipelineDurationCommonSource } from "Pipeline/Analytics/PipelineDurationCommon/Sources/PipelineDurationCommonSource";
import { IPipelineStepDurationTrend, IStageStepsDurationData, IStepDurationData } from "Pipeline/Analytics/PipelineStepsDurationCard/Types";
import { IVssPageContext } from "VSS/Platform/Context";

interface IODataResult {
    BuildCompletedDateSK?: number;
    PipelineRunCompletedDateSK?: number;
    TaskDisplayName: string;
    TotalDuration: number;
    PipelineJob: {
        StageName: string;
    };
}

export class PipelineStepsDurationCardSource extends PipelineDurationCommonSource {
    protected _defaultStage: string = "__default";
    constructor(pageContext: IVssPageContext) {
        super("PipelineAnalytics_PipelineStepsDurationCardSource", pageContext);
    }

    public queryPipelineStepDurationTrendData(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration
    ): Promise<IPipelineStepDurationTrend[]> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const groupByFields = `${this._getPipelineJobStageName()},${this._getGroupByTask()} , ${this._getBuildCompletedDateSK()}`;
        const query = new ODataQuery()
            .filter(
                expr(this._getPipelineContextFilter(reportContext, true))
                    .and.expr(getDateFilter(confValues.trendBy, confValues.period, `${PipelineEntities.PipelineRunCompletedDateSK}`))
                    .reverseExprStatus()
                    .expr(this._getBranchFilter(confValues.branchItems))
                    .and.expr(this.getDurationBuildOutcomeCondition())
                    .and.expr(this._getTaskActivityQueryFilter())
            )
            .compute(this._getDurationPercentileWithGroupByForCompute(percentile_value, this._getActivityDurationSeconds(), groupByFields))
            .groupBy(this._getDurationPercentile(), groupByFields)
            .aggregate(`$count as TotalCount`);

        queryOptions.$apply = query.apply;

        return this.getPipelineStepDurationData(queryOptions);
    }

    public queryTopStepDurationData(reportContext: IReportContext, confValues: ITrendChartReportConfiguration): Promise<IStageStepsDurationData> {
        const projectId = super._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const query = new ODataQuery()
            .filter(
                expr(this._getPipelineContextFilter(reportContext, true))
                    .and.expr(getDateFilter(confValues.trendBy, confValues.period, `${PipelineEntities.PipelineRunCompletedDateSK}`))
                    .reverseExprStatus()
                    .expr(this._getBranchFilter(confValues.branchItems))
                    .and.expr(this.getDurationBuildOutcomeCondition())
                    .and.expr(this._getTaskActivityQueryFilter())
            )
            .compute(
                this._getDurationPercentileWithGroupByForCompute(
                    percentile_value,
                    this._getActivityDurationSeconds(),
                    `${this._getPipelineJobStageName()}, ${this._getGroupByTask()}`
                )
            )
            .groupBy(this._getDurationPercentile(), this._getPipelineJobStageName(), this._getGroupByTask())
            .aggregate(this._getDurationPercentileForAggregation())
            .orderBy("DurationPercentile", "desc");

        queryOptions.$apply = query.apply;
        queryOptions.$orderby = query.orderby;
        return this.getTopDurationData(queryOptions);
    }

    private async getPipelineStepDurationData(queryOptions: ODataQueryOptions): Promise<IPipelineStepDurationTrend[]> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        const stepsDurationTrendMap: { [date: string]: { stepDurationData: { [stage: string]: IStepDurationData[] } } } = {};
        let steps: IStepDurationData = { stepName: "", stepDuration: 0 };
        const dataValues = data.value as IODataResult[];
        if (dataValues && dataValues.length > 0) {
            dataValues.forEach(d => {
                let stage = this._parsePipelineStageName(d);
                if (!stage || stage == this._defaultStage) {
                    stage = CommonResources.DefaultStageName;
                }
                const date = DateSKParser.parseDateSKAsDateString(d.BuildCompletedDateSK || d.PipelineRunCompletedDateSK);
                steps = {
                    stepName: stage + "\\" + this._parseTaskDisplayName(d),
                    stepDuration: this._parseDurationPercentile(d)
                };
                if (stepsDurationTrendMap[date]) {
                    stepsDurationTrendMap[date].stepDurationData[CommonResources.AllStageName]
                        ? stepsDurationTrendMap[date].stepDurationData[CommonResources.AllStageName].push(steps)
                        : (stepsDurationTrendMap[date].stepDurationData[CommonResources.AllStageName] = [steps]);
                    stepsDurationTrendMap[date].stepDurationData[stage]
                        ? stepsDurationTrendMap[date].stepDurationData[stage].push(steps)
                        : (stepsDurationTrendMap[date].stepDurationData[stage] = [steps]);
                } else {
                    stepsDurationTrendMap[date] = { stepDurationData: { [stage]: [steps] } };
                    stepsDurationTrendMap[date].stepDurationData[CommonResources.AllStageName] = [steps];
                }
            });
        }
        const pipelineStepDurationTrendData: IPipelineStepDurationTrend[] = [];

        Object.keys(stepsDurationTrendMap).map(data =>
            pipelineStepDurationTrendData.push({ date: data, stepsDurationData: stepsDurationTrendMap[data].stepDurationData })
        );

        return pipelineStepDurationTrendData;
    }

    private async getTopDurationData(queryOptions: ODataQueryOptions): Promise<IStageStepsDurationData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        let steps: IStepDurationData = { stepName: "", stepDuration: 0 };
        const stageStepsDuration: IStageStepsDurationData = { stepDurationData: {} };
        const dataValues = data.value as any[];
        const stageMetricsList: { [key: string]: IStageMetrics } = {};

        if (dataValues && dataValues.length > 0) {
            dataValues.forEach(d => {
                let stage = this._parsePipelineStageName(d);
                const stageMetrics = this._parseStageData(stage);
                stage = stageMetrics.name;

                if (!stageMetricsList[stageMetrics.fullName]) {
                    stageMetricsList[stageMetrics.fullName] = stageMetrics;
                }
                stageMetricsList[stageMetrics.fullName].dataCount++;

                steps = {
                    stepName: stage + "\\" + this._parseTaskDisplayName(d),
                    stepDuration: this._parseDurationPercentile(d)
                };

                if (stageStepsDuration.stepDurationData[CommonResources.AllStageName]) {
                    stageStepsDuration.stepDurationData[CommonResources.AllStageName].stepsData.push(steps);
                    stageStepsDuration.stepDurationData[CommonResources.AllStageName].totalStageStepDuration += steps.stepDuration;
                } else {
                    stageStepsDuration.stepDurationData[CommonResources.AllStageName] = {
                        stepsData: [steps],
                        totalStageStepDuration: steps.stepDuration
                    };
                }

                if (stageStepsDuration.stepDurationData[stage]) {
                    stageStepsDuration.stepDurationData[stage].stepsData.push(steps);
                    stageStepsDuration.stepDurationData[stage].totalStageStepDuration += steps.stepDuration;
                } else {
                    stageStepsDuration.stepDurationData[stage] = { stepsData: [steps], totalStageStepDuration: steps.stepDuration };
                }
            });
        }

        stageStepsDuration.stages = Object.keys(stageMetricsList).map(stageData => {
            return stageMetricsList[stageData];
        });
        return stageStepsDuration;
    }
}
